import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  getProviderBatch,
  getProviderBatchAudit,
  getProviderBatchEvidenceReadiness,
  getProviderBatchExceptions,
  getProviderBatchItems,
} from '../../api/providerApi';
import {
  PackageAssetsTable,
  mapProviderItemToAssetRow,
} from '../../components/package/PackageAssetsTable';
import { ActivityFeed } from '../../components/activity/ActivityFeed';
import { PackageViewSwitch } from '../../components/package/PackageViewSwitch';
import { ErrorBanner } from '../../components/ui/ErrorBanner';
import { StatusPill } from '../../components/ui/StatusPill';
import { buttonBase, buttonVariants } from '../../components/ui/Button';
import { isUuid, formatDateTime, formatRelativeTime } from '../../utils/format';
import { LifecycleInfoPopover } from '../../components/ui/LifecycleInfoPopover';
import { nextActionLabel, packageStatusLabel } from './providerReviewLabels';

export function providerLifecycleSteps(detail) {
  const status = detail?.package_status;
  const classified = status === 'CLASSIFIED' || status === 'CERTIFIED';
  const certified = status === 'CERTIFIED';
  const authorityDecided =
    detail?.authority_status === 'APPROVED' || detail?.authority_status === 'REJECTED';
  const routed =
    detail?.next_action === 'AWAITING_CUSTODIAN' ||
    detail?.next_action === 'AWAITING_ADMISSION';
  const validated = detail?.upload_status === 'COMPLETE';
  const evidenceTotal = Number(detail?.evidence_total);
  const evidenceSatisfied = Number(detail?.evidence_satisfied);
  const validationDetail =
    validated && Number.isFinite(evidenceTotal) && evidenceTotal > 0
      ? `Passed  · ${Number.isFinite(evidenceSatisfied) ? evidenceSatisfied : 0}/${evidenceTotal}`
      : validated
        ? 'Complete'
        : undefined;
  const reviewComplete = certified && detail?.next_action !== 'AWAITING_PROVIDER_REVIEW';
  return [
    {
      id: 'recv',
      label: 'Received',
      shortLabel: 'Received',
      complete: true,
      detail: formatRelativeTime(detail?.created_at || detail?.updated_at),
    },
    {
      id: 'val',
      label: 'Validated',
      shortLabel: 'Validated',
      complete: validated,
      detail: validationDetail,
    },
    {
      id: 'cls',
      label: 'Classified',
      shortLabel: 'Classified',
      complete: classified,
      detail: classified ? packageStatusLabel(status) : undefined,
    },
    {
      id: 'rev',
      label: 'Provider review',
      shortLabel: 'Provider',
      complete: reviewComplete,
      detail: reviewComplete ? 'Complete' : undefined,
    },
    {
      id: 'auth',
      label: 'Authority gate',
      shortLabel: 'Authority',
      complete: authorityDecided,
      detail: authorityDecided
        ? detail?.authority_status === 'APPROVED'
          ? 'Approved'
          : detail?.authority_status === 'REJECTED'
            ? 'Rejected'
            : detail?.authority_status
        : undefined,
    },
    {
      id: 'route',
      label: 'Routed to custodian',
      shortLabel: 'Routed',
      complete: routed,
      detail: routed ? 'Routed' : undefined,
    },
  ];
}

export function ProviderPackageDetailPage() {
  const { packageId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get('view') === 'assets' ? 'assets' : 'audit';
  const validId = isUuid(packageId);
  const [detail, setDetail] = useState(null);
  const [items, setItems] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [evidence, setEvidence] = useState(null);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(validId);
  const [error, setError] = useState(
    validId ? '' : 'Open a package from the live list. This id is not a package UUID.'
  );
  const [reloadToken, setReloadToken] = useState(0);

  function setView(nextView) {
    const next = new URLSearchParams(searchParams);
    if (nextView === 'assets') {
      next.set('view', 'assets');
    } else {
      next.delete('view');
    }
    setSearchParams(next, { replace: true });
  }

  useEffect(() => {
    if (!validId) return undefined;
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError('');
      try {
        const [detailResponse, itemRows, exceptionResponse, evidenceResponse, auditResponse] =
          await Promise.all([
            getProviderBatch(packageId),
            getProviderBatchItems(packageId),
            getProviderBatchExceptions(packageId),
            getProviderBatchEvidenceReadiness(packageId),
            getProviderBatchAudit(packageId),
          ]);
        if (cancelled) return;
        setDetail(detailResponse);
        setItems(itemRows || []);
        setExceptions(exceptionResponse.items || []);
        setEvidence(evidenceResponse);
        setAudit(auditResponse.items || []);
      } catch (requestError) {
        if (cancelled) return;
        setError(requestError.message || 'Unable to load this package.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [packageId, validId, reloadToken]);

  const assetRows = useMemo(
    () => items.map((item) => mapProviderItemToAssetRow(item, detail)),
    [items, detail]
  );

  return (
    <div className="space-y-5">
      {error ? (
        <ErrorBanner
          message={error}
          onRetry={validId ? () => setReloadToken((value) => value + 1) : undefined}
        />
      ) : null}

      {loading ? (
        <section className="rounded-2xl border border-eorig-border bg-white p-5 text-sm text-eorig-muted shadow-eorig-card">
          Loading package…
        </section>
      ) : null}

      {detail ? (
        <>
          <section className="rounded-2xl border border-eorig-border bg-white p-5 shadow-eorig-card">
            <LifecycleInfoPopover steps={providerLifecycleSteps(detail)}>
              {(lifecycleTrigger) => (
                <>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-start gap-2">
                        <h2 className="min-w-0 text-base font-semibold text-eorig-ink">
                          {detail.reference_number || detail.batch_id}
                        </h2>
                        {lifecycleTrigger}
                      </div>
                      <p className="mt-1 text-xs text-eorig-muted">
                        {detail.program} · {detail.submitter_org}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <StatusPill
                        value={detail.package_status}
                        label={packageStatusLabel(detail.package_status)}
                      />
                      <StatusPill value={detail.authority_status} />
                      <StatusPill
                        value={detail.next_action}
                        label={nextActionLabel(detail.next_action)}
                      />
                    </div>
                  </div>
                  <p className="mt-3 text-xs text-eorig-muted">
                    Updated {formatDateTime(detail.updated_at)} · {detail.items_count} items ·{' '}
                    {detail.open_exceptions} open exceptions · evidence {detail.evidence_satisfied}/
                    {detail.evidence_total}
                  </p>
                </>
              )}
            </LifecycleInfoPopover>
          </section>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <PackageViewSwitch value={view} onChange={setView} />
            <span className="text-[11px] text-eorig-muted">
              {view === 'audit'
                ? 'Package audit detail'
                : 'Open a loan to review documents and extracted fields'}
            </span>
          </div>

          {view === 'audit' ? (
            <>
              <div className="grid gap-4 lg:grid-cols-2">
                <section className="rounded-2xl border border-eorig-border bg-white p-5 shadow-eorig-card">
                  <h3 className="text-sm font-semibold text-eorig-ink">Evidence readiness</h3>
                  <p className="mt-1 text-xs text-eorig-muted">
                    {evidence
                      ? `${evidence.satisfied_count}/${evidence.total_count} satisfied`
                      : 'No evidence payload returned.'}
                  </p>
                  <ul className="mt-3 space-y-2 text-sm">
                    {(evidence?.items || []).map((item) => (
                      <li key={item.id} className="flex items-center justify-between gap-2">
                        <span className="text-eorig-ink">{item.label}</span>
                        <StatusPill
                          value={item.satisfied ? 'COMPLETED' : 'OPEN'}
                          label={item.satisfied ? 'Satisfied' : 'Open'}
                        />
                      </li>
                    ))}
                  </ul>
                </section>
                <section className="rounded-2xl border border-eorig-border bg-white p-5 shadow-eorig-card">
                  <h3 className="text-sm font-semibold text-eorig-ink">Exceptions</h3>
                  <ul className="mt-3 space-y-2 text-sm">
                    {exceptions.length ? (
                      exceptions.map((item) => (
                        <li key={item.id} className="rounded-xl border border-eorig-border px-3 py-2">
                          <p className="font-medium text-eorig-ink">{item.code}</p>
                          <p className="text-xs text-eorig-muted">
                            {item.severity} · {item.status}
                            {item.description ? ` · ${item.description}` : ''}
                          </p>
                        </li>
                      ))
                    ) : (
                      <li className="text-eorig-muted">No exceptions on this package.</li>
                    )}
                  </ul>
                </section>
              </div>

              <section className="rounded-2xl border border-eorig-border bg-white p-5 shadow-eorig-card">
                <h3 className="text-sm font-semibold text-eorig-ink">Activity</h3>
                <div className="mt-4">
                  <ActivityFeed items={audit} emptyMessage="No audit events returned." />
                </div>
              </section>
            </>
          ) : (
            <section className="space-y-3">
              <h3 className="text-sm font-semibold text-eorig-ink">Loans</h3>
              <PackageAssetsTable
                rows={assetRows}
                loanHref={(row) => `/provider/assets/${row.id}`}
                emptyMessage="No loans in this package yet."
              />
            </section>
          )}
        </>
      ) : null}

      <Link
        to="/provider/packages"
        className={`${buttonBase} ${buttonVariants.ghost}`}
      >
        ← Back to packages
      </Link>
    </div>
  );
}
